import React, { useState, useEffect, useRef } from 'react';
import { FilledButton } from './Button';
import { FaChevronDown } from "react-icons/fa6";
import { IoChevronDown } from 'react-icons/io5';

type DropdownMenuProps = {
    options: any[]; // List of possible values to select
    optionsToTextMapper?: (option: any) => string;
    onSelect: (value: string) => void; // Callback when an option is selected
    label?: string; // Optional label for the dropdown
    defaultValue?: any; // Pre-chosen value
};




const DropdownMenu: React.FC<DropdownMenuProps & { variant: "filled" | "outlined" | "flat" }> = ({
    variant,
    options,
    optionsToTextMapper = undefined,
    onSelect,
    label,
    defaultValue,
}) => {
    const [isOpen, setIsOpen] = useState(false); // State to toggle dropdown visibility
    const [selectedOption, setSelectedOption] = useState<string | null>(defaultValue !== undefined ? defaultValue : null);
    const dropdownRef = useRef<HTMLDivElement>(null); // Ref to track the dropdown element
    const menuRef = useRef<HTMLDivElement>(null);
    const [lastKeyPress, setLastKeyPress] = useState<number>(0);
    const [searchString, setSearchString] = useState<string>('');
    const [matchedOption, setMatchedOption] = useState<string | null>(null);
    const [currentMatchIndex, setCurrentMatchIndex] = useState<number>(0);

    const variantClasses = {
        filled: 'bg-white',
        outlined: `bg-white border border-gray-500 focus:ring-primary focus:border-primary hover:bg-gray-50
        ${isOpen
                ? 'border-primary bg-neutral-100'
                : ''}`,
        flat: `bg-primary-50 focus:ring-primary focus:border-primary hover:bg-primary-100
        ${isOpen
                ? 'border-primary bg-neutral-100'
                : ''}`,
    };

    const variantItemClasses = {
        filled: '',
        outlined: `hover:bg-neutral-100`,
    };

    const isSelectedElement = (elem: string | null) => {
        if (optionsToTextMapper) {
            return optionsToTextMapper(elem) === optionsToTextMapper(selectedOption)
        } else {
            return elem === selectedOption
        }
    }

    // Ensure the default value is valid
    useEffect(() => {
        if (defaultValue && options.includes(defaultValue) && selectedOption === null) {
            setSelectedOption(defaultValue);
        }
    }, [defaultValue, options, selectedOption]);


    const handleSelect = (option: string, event: React.MouseEvent) => {
        event.stopPropagation(); // Prevent event bubbling
        const newOption = option;
        setSelectedOption(newOption);
        onSelect(newOption);
        setIsOpen(false); // Close dropdown after selection
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleKeyPress = (event: KeyboardEvent) => {
        if (!isOpen) return;

        if (event.key === 'Enter') {
            if (matchedOption) {
                handleSelect(matchedOption, {} as React.MouseEvent);
                return;
            }
        }

        const currentTime = new Date().getTime();
        const isSameKey = currentTime - lastKeyPress < 500 && event.key === searchString[searchString.length - 1];

        let newSearchString = searchString;
        if (!isSameKey) {
            newSearchString = currentTime - lastKeyPress > 500
                ? event.key.toLowerCase()
                : searchString + event.key.toLowerCase();
            setSearchString(newSearchString);
            setCurrentMatchIndex(0); // Reset index for new search
        }

        setLastKeyPress(currentTime);

        const matchingOptions = options.filter(option => {
            const text = optionsToTextMapper ? optionsToTextMapper(option) : option;
            return text.toLowerCase().startsWith(newSearchString);
        });

        if (matchingOptions.length > 0) {
            let newIndex = isSameKey ? (currentMatchIndex + 1) % matchingOptions.length : 0;
            setCurrentMatchIndex(newIndex);
            const matchingOption = matchingOptions[newIndex];
            setMatchedOption(matchingOption);

            if (menuRef.current) {
                const optionElement = menuRef.current.querySelector(`[data-value="${matchingOption}"]`);
                if (optionElement) {
                    optionElement.scrollIntoView({ block: 'nearest' });
                }
            }
        } else {
            setMatchedOption(null);
            setCurrentMatchIndex(0);
        }
    };

    useEffect(() => {
        if (isOpen) {
            document.addEventListener('keydown', handleKeyPress);
            return () => document.removeEventListener('keydown', handleKeyPress);
        }
    }, [isOpen, searchString, lastKeyPress]);

    const handleMouseEnter = (option: string) => {
        setMatchedOption(option);
        const matchingOptions = options.filter(opt => {
            const text = optionsToTextMapper ? optionsToTextMapper(opt) : opt;
            return text.toLowerCase().startsWith(searchString);
        });
        const index = matchingOptions.indexOf(option);
        if (index !== -1) {
            setCurrentMatchIndex(index);
        }
    };

    return (
        <div className="relative" ref={dropdownRef}>
            {/* Label */}
            {label && <label className={`mb-2 block text-sm/6 font-medium text-gray-900}`}>{label}</label>}

            {/* Dropdown Button */}
            <div
                onClick={() => setIsOpen(!isOpen)}
                className={`font-medium text-base flex flex-row items-center justify-between py-1.5 px-3 cursor-pointer rounded-md
                ${variant === 'filled' ?
                        variantClasses.filled :
                        variant === 'outlined' ?
                            variantClasses.outlined :
                            variant === 'flat' ?
                                variantClasses.flat : ''
                    } 
                `}
            >
                <span>{selectedOption !== undefined
                    ? optionsToTextMapper
                        ? optionsToTextMapper(selectedOption)
                        : selectedOption
                    : 'Select an option'}</span>
                <IoChevronDown
                    className={`ml-2 w-5 h-5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                />
            </div>


            {/* Dropdown Menu */}
            {isOpen && (
                <div
                    ref={menuRef}
                    className={`w-full absolute z-10 mt-2 rounded-lg shadow-lg max-h-56 overflow-auto transition-all duration-200 ease-in-out bg-white border border-neutral-200`}
                >
                    {options.map((option, index) => (
                        <div
                            key={index}
                            data-value={option}
                            onClick={(event) => handleSelect(option, event)}
                            onMouseEnter={() => handleMouseEnter(option)}
                            onMouseLeave={() => setMatchedOption(null)}
                            className={`m-2 px-6 py-2 rounded-md cursor-pointer transition-all duration-150
                            ${variant === 'filled' ?
                                    variantItemClasses.filled :
                                    variant === 'outlined' ?
                                        variantItemClasses.outlined :
                                        variant === 'flat' ?
                                            variantItemClasses.outlined : ''}
                                        
                                        ${isSelectedElement(option)
                                    ? 'bg-neutral-200'
                                    : ''}
                                        
                                        ${matchedOption === option
                                    ? 'bg-neutral-100'
                                    : ''}
                                }`
                            }                        >

                            {optionsToTextMapper
                                ? <p className="caption2">{optionsToTextMapper(option)}</p>
                                : <p className="caption2">{option}</p>
                            }

                        </div>
                    ))}
                </div>
            )
            }
        </div >
    );
};

export const FilledDropdownMenu: React.FC<DropdownMenuProps> = (props) => <DropdownMenu {...props} variant="filled" />;
export const OutlinedDropdownMenu: React.FC<DropdownMenuProps> = (props) => <DropdownMenu {...props} variant="outlined" />;
export const FlatDropdownMenu: React.FC<DropdownMenuProps> = (props) => <DropdownMenu {...props} variant="flat" />;
